import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Layers, RotateCcw, Plus, CheckCircle2, AlertTriangle, Droplets, Wine } from "lucide-react";
import { toast } from "react-toastify";

import "../styles/pallets.css";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { apiFetch } from "../api/apiFetch";
import {
    silentPrintZplRaw,
    silentPrint,
    browserPrint,
    buildSalePalletLabel,
    buildSalePalletZpl,
    isPrintAgentAvailable,
} from "../components/helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
    isSavedNetworkPrinterOnline,
} from "../components/helpers/networkPrint";
import PrinterSelectModal from "../components/PrinterSelectModal";

// Goods In › Pallets — desktop + printer. The operator has already booked
// the SKUs onto a colour tote (Book In) and closed it. Here they pick the
// colour (Black = unmanifested, no SKUs), the pallet type, the size, the
// supplier (every active supplier, Amazon preselected) and any notes;
// the BE issues the next label for this warehouse + colour (PD-RED1),
// creates the location (barcode = SKU) and the product (cost 0), and the
// label prints. Put Away on the handheld then moves the tote's SKUs into
// the pallet location.

export type PalletColour = "BLACK" | "GREY" | "RED" | "YELLOW" | "DSD";
// 'not_listed' reads "Ready to List" (Alex, 2026-10-15); the value is unchanged.
// 'error' = "Error - Needs Resolving": parked by an admin until sorted.
// The PORTAL_STATUSES are set by the Portal, never by hand:
//   'mixed_supplier'     the products put away onto the pallet carry more
//                        than one supplier
//   'different_supplier' they carry one supplier, and it is not the label's
//   'empty'              nothing at the pallet's location (never BLACK)
//   'duplicate_sku'      a product SKU on the pallet is also on another
//                        pallet (Alex, 2026-10-28); duplicate_skus on the
//                        row says which SKUs and which pallets
// PALLET_STATUSES is what a person may choose (the dropdown and the bulk
// selector): since 2026-10-21 that includes Mixed / Different Supplier by
// hand (Alex); Empty and Duplicate SKU only ever show as a row's current
// value. A choice
// made by hand sticks (the BE leaves it alone until a person moves it).
// "listing" (Alex, 2026-10-22): the person listing has claimed the pallet
// — their batch, locked to them until they move it to Listed or back.
export type PalletStatus = "not_listed" | "listing" | "listed" | "error" | "mixed_supplier" | "different_supplier" | "empty" | "duplicate_sku";
export const PALLET_STATUSES: PalletStatus[] = ["not_listed", "listing", "listed", "error", "mixed_supplier", "different_supplier"];
export const PORTAL_STATUSES: PalletStatus[] = ["mixed_supplier", "different_supplier", "empty", "duplicate_sku"];
// One entry per SKU the pallet shares with another pallet: which SKU, and
// the other pallet(s) it is also on (A–Z). Set by the BE with the
// Duplicate SKU verdict; null otherwise.
export interface DuplicateSku { sku: string; pallets: string[] }
export const MIXED_SUPPLIER: PalletStatus = "mixed_supplier";
export const isPortalStatus = (s: PalletStatus) => PORTAL_STATUSES.includes(s);
// A storage pallet: never listed for sale, status fixed at Listed from creation.
export const isStorageColour = (c: PalletColour | string) => c === "DSD";

// The supplier step always starts on this, and it is always offered even
// if the suppliers table does not (yet) carry it.
export const DEFAULT_SUPPLIER = "Amazon";
const NOTES_MAX = 1000;

// DSD (Alex, 2026-10-16): data-device STORAGE pallets — stock sits on them
// like Red / Yellow / Grey while the devices wait to be wiped, but they are
// never listed for sale: born Listed, status fixed, white tile.
export const PALLET_COLOURS: { key: PalletColour; labelKey: string; swatch: string; text: string; hintKey?: string; outline?: boolean }[] = [
    { key: "BLACK",  labelKey: "colour_black",  swatch: "#111827", text: "#ffffff", hintKey: "pallets_colour_black_hint" },
    { key: "GREY",   labelKey: "colour_grey",   swatch: "#6b7280", text: "#ffffff" },
    { key: "RED",    labelKey: "colour_red",    swatch: "#ef4444", text: "#ffffff" },
    { key: "YELLOW", labelKey: "colour_yellow", swatch: "#eab308", text: "#111827" },
    { key: "DSD",    labelKey: "pallets_colour_dsd", swatch: "#ffffff", text: "#111827", hintKey: "pallets_colour_dsd_hint", outline: true },
];

interface PalletSettings {
    warehouse: string;
    types: string[];
    sizes: string[];
    allow_height: boolean;
    // Settings › Goods In › Pallets › Contents warnings: adds the liquids /
    // glass step; optional so an older BE (no key) simply hides it.
    hazard_flags?: boolean;
}

// Contents warnings (Alex, 2026-10-29): what the operator ticks between
// Supplier and Notes when the warehouse has the toggle on. Each one prints
// as a warning triangle on the label and nowhere else — not posted, not
// stored, not exported — so a reprint from the recent list prints without
// them; only the pallet just created keeps them for its reprint button.
export interface PalletHazards {
    liquids: boolean;
    glass: boolean;
}
export const NO_HAZARDS: PalletHazards = { liquids: false, glass: false };
export const PALLET_HAZARDS: { key: keyof PalletHazards; labelKey: string; Icon: typeof Droplets }[] = [
    { key: "liquids", labelKey: "pallets_hazard_liquids", Icon: Droplets },
    { key: "glass",   labelKey: "pallets_hazard_glass",   Icon: Wine },
];

export interface PalletRow {
    pallet_sku: string;
    warehouse: string;
    colour: PalletColour;
    seq: number;
    pallet_type: string | null;
    size_mode: "fixed" | "height" | "none";
    size_name: string | null;
    height_cm: number | null;
    size_label: string;
    palletways_type?: string | null;
    supplier_name?: string | null;
    notes?: string | null;
    // Who last changed the note from the admin table and when; both null
    // while the Goods In note stands as printed.
    notes_updated_by_name?: string | null;
    notes_updated_at?: string | null;
    status: PalletStatus;
    created_by_name: string | null;
    created_at: string;
    print_count: number;
    voided_at: string | null;
    // One-time put-away tote printed as the label's second barcode.
    tote_id?: string | null;
    // Admin list only: what has been put away onto the pallet so far.
    units?: number;
    skus?: number;
    cost_value?: number;
    rrp_value?: number;
    // Weight in grams, worked out by the BE from the products on the
    // pallet (units × each product's weight) — never entered. null when
    // nothing on it has a weight; unweighed_units counts the units whose
    // product has none, so above 0 the weight is only part of the truth.
    weight_grams?: number | null;
    unweighed_units?: number;
    // "volumetric": the warehouse is on volumetric weight and the figure is
    // footprint × height ÷ divisor, with the sum in weight_formula
    // ("120 × 80 × 150 ÷ 4000 × 1000"); "contents" (or absent): the sum of
    // what is on the pallet.
    weight_basis?: "volumetric" | "contents";
    weight_formula?: string | null;
    // Set on a Listed pallet whose figures above are the snapshot taken
    // when it was listed (the stock sells and leaves the location, the
    // figures stay). null = live sums.
    contents_locked_at?: string | null;
    // Distinct suppliers on the products put away onto the pallet (A–Z)
    // and whether they disagree with supplier_name — the Mixed Supplier
    // rule, worked out for every row; only a Not Listed pallet has its
    // status changed by it.
    product_suppliers?: string[];
    supplier_mismatch?: boolean;
    // The Portal verdict an admin dismissed by hand; null when the
    // Portal's say-so stands.
    status_override?: PalletStatus | null;
    // Which SKUs on the pallet are also on another pallet, and which
    // pallets; null unless the Portal's Duplicate SKU verdict stands.
    duplicate_skus?: DuplicateSku[] | null;
    // Whose Listing batch the pallet is in (status "listing") and who
    // listed it (status "listed"); null otherwise.
    listing_by_name?: string | null;
    listed_by_name?: string | null;
    // Where the pallet stands: the storage location holding its own unit,
    // by display name; null until Put Away lands it somewhere.
    location_name?: string | null;
    location_barcode?: string | null;
}

type SizeMode = "fixed" | "height";

const ERROR_KEYS: Record<string, string> = {
    colour_invalid: "pallets_err_colour_invalid",
    type_required: "pallets_err_type_required",
    size_required: "pallets_err_size_required",
    size_invalid: "pallets_err_size_invalid",
    height_invalid: "pallets_err_height_invalid",
    height_not_allowed: "pallets_err_height_not_allowed",
    supplier_required: "pallets_err_supplier_required",
    location_exists: "pallets_err_location_exists",
    product_exists: "pallets_err_product_exists",
    seq_taken: "pallets_err_seq_taken",
};

// One label goes on each side of the pallet, so every print (create and
// reprint) runs four copies.
export const PALLET_LABEL_COPIES = 4;

// Network printer (raw 4×6 ZPL on a Zebra, HTML on anything else) → local
// Zebra (raw 4×6 ZPL with logo + both barcodes, agent ≥ 1.6) → local
// non-Zebra (HTML) → browser print dialog. The browser dialog is the only
// path that cannot be told the copy count; the operator sets it in the dialog.
export const printPalletLabel = async (pallet: PalletRow, operator?: string | null, hazards?: PalletHazards | null): Promise<boolean> => {
    const opts = {
        warehouse: pallet.warehouse,
        colour: pallet.colour,
        palletType: pallet.pallet_type,
        sizeLabel: pallet.size_label,
        createdAt: pallet.created_at,
        operator: pallet.created_by_name ?? operator ?? null,
        toteId: pallet.tote_id ?? null,
        hazards: hazards && (hazards.liquids || hazards.glass) ? hazards : null,
    };
    const html = buildSalePalletLabel(pallet.pallet_sku, opts);
    const zpl = buildSalePalletZpl(pallet.pallet_sku, opts);
    const saved = getSavedNetworkPrinter();
    if (saved) {
        // The job carries BOTH renderings of the design and NO barcodeValue
        // (mirrors pallet-receiving.tsx): a barcodeValue makes the agent print
        // its own generic 65×38 barcode layout instead of the 4×6 label, which
        // is what remote reprints from Admin › Pallets Created used to get.
        const r = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, PALLET_LABEL_COPIES, null, false, { zpl });
        if (r.success) return true;
    }
    const zebra = await silentPrintZplRaw(zpl, PALLET_LABEL_COPIES);
    if (zebra.success) return true;
    const local = await silentPrint(html, PALLET_LABEL_COPIES);
    if (local.success) return true;
    const browser = await browserPrint(html);
    return browser.success;
};

const Pallets = () => {
    usePageTitle("pallets");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "" };
    const warehouse: string = userObject.warehouse ?? "";

    const [settings, setSettings] = useState<PalletSettings | null>(null);
    const [colour, setColour] = useState<PalletColour | null>(null);
    const [palletType, setPalletType] = useState<string | null>(null);
    const [sizeMode, setSizeMode] = useState<SizeMode>("fixed");
    const [sizeName, setSizeName] = useState<string | null>(null);
    const [heightCm, setHeightCm] = useState("");
    const [suppliers, setSuppliers] = useState<string[]>([DEFAULT_SUPPLIER]);
    const [supplier, setSupplier] = useState(DEFAULT_SUPPLIER);
    const [notes, setNotes] = useState("");
    const [hazards, setHazards] = useState<PalletHazards>(NO_HAZARDS);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<{ pallet: PalletRow; printed: boolean; hazards: PalletHazards | null } | null>(null);
    const [recent, setRecent] = useState<PalletRow[]>([]);
    const [reprinting, setReprinting] = useState<string | null>(null);

    // Printer: network printer saved for this warehouse, else the local
    // agent. The header button opens the same modal the scan sheet uses.
    const [printMode, setPrintMode] = useState<"local" | "network" | null>(null);
    const [printerOnline, setPrinterOnline] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);

    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const bounce = useCallback((res: Response) => {
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return true;
        }
        return false;
    }, [navigate]);

    const detectPrinter = useCallback(async () => {
        if (getSavedNetworkPrinter()) {
            const online = await isSavedNetworkPrinterOnline();
            if (online) {
                if (!mountedRef.current) return;
                setPrintMode("network");
                setPrinterOnline(true);
                return;
            }
        }
        const localOk = await isPrintAgentAvailable();
        if (!mountedRef.current) return;
        setPrintMode(localOk ? "local" : null);
        setPrinterOnline(localOk);
    }, []);

    useEffect(() => { detectPrinter(); }, [detectPrinter]);

    // Supplier dropdown: every active supplier from the canonical
    // /suppliers table (same source as Products / Boxes), with Amazon
    // guaranteed to be present since it is the default.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/suppliers?status=active&page=0&page_size=500`, {});
                if (bounce(res)) return;
                if (!res.ok) return;
                const data = await res.json().catch(() => null);
                const names: string[] = Array.isArray(data?.suppliers)
                    ? data.suppliers.map((s: { name?: string }) => s.name).filter((n: unknown): n is string => typeof n === "string" && n.trim() !== "")
                    : [];
                if (!names.includes(DEFAULT_SUPPLIER)) names.push(DEFAULT_SUPPLIER);
                names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
                if (mountedRef.current) setSuppliers(names);
            } catch { /* keep the default-only list */ }
        })();
    }, [bounce]);

    const loadRecent = useCallback(async () => {
        try {
            const params = new URLSearchParams({ warehouse, limit: "10" });
            const res = await apiFetch(`/pallets/recent?${params.toString()}`, {});
            if (bounce(res)) return;
            if (!res.ok) return;
            const data = await res.json().catch(() => null);
            if (mountedRef.current && data && Array.isArray(data.rows)) setRecent(data.rows);
        } catch { /* leave as-is */ }
    }, [warehouse, bounce]);

    useEffect(() => {
        (async () => {
            try {
                const params = new URLSearchParams({ warehouse });
                const res = await apiFetch(`/pallets/settings?${params.toString()}`, {});
                if (bounce(res)) return;
                const data = await res.json().catch(() => null);
                if (!res.ok || !data) {
                    toast.error(t("server_error"), { toastId: "pallets-settings" });
                    return;
                }
                if (!mountedRef.current) return;
                setSettings(data);
                // Only one way to size the pallet? Preselect it.
                if (data.sizes.length === 0 && data.allow_height) setSizeMode("height");
            } catch {
                toast.error(t("server_error"), { toastId: "pallets-settings" });
            }
        })();
        loadRecent();
    }, [warehouse, bounce, loadRecent, t]);

    const typeStep = !!settings && settings.types.length > 0;
    const sizeStep = !!settings && (settings.sizes.length > 0 || settings.allow_height);
    const hazardStep = !!settings && settings.hazard_flags === true;
    const heightValue = Number(heightCm);
    const heightOk = heightCm.trim() !== "" && Number.isFinite(heightValue) && heightValue > 0 && heightValue <= 300;
    const sizeChosen = !sizeStep || (sizeMode === "fixed" ? !!sizeName : heightOk);
    const supplierChosen = supplier.trim() !== "";
    const complete = !!colour && (!typeStep || !!palletType) && sizeChosen && supplierChosen;

    const sizeSummary = !sizeStep ? "" : sizeMode === "fixed" ? (sizeName ?? "") : (heightOk ? `${heightValue} cm` : "");
    const notesTrimmed = notes.trim();
    const hazardNames = (h: PalletHazards | null | undefined) => PALLET_HAZARDS.filter(x => h?.[x.key]).map(x => t(x.labelKey));
    const hazardSummary = hazardNames(hazards).join(" + ");

    // Steps are numbered by what is actually shown: type, size and the
    // contents warning can be switched off per warehouse, supplier and
    // notes are always there.
    const stepNo = { colour: 1, type: 2, size: typeStep ? 3 : 2 };
    const supplierNo = stepNo.size + (sizeStep ? 1 : 0);
    const hazardNo = supplierNo + 1;
    const notesNo = supplierNo + (hazardStep ? 2 : 1);
    const stepTitle = (n: number, key: string) => `${n}. ${t(key)}`;

    const reset = () => {
        setColour(null);
        setPalletType(null);
        setSizeName(null);
        setHeightCm("");
        setSupplier(DEFAULT_SUPPLIER);
        setNotes("");
        setHazards(NO_HAZARDS);
        setResult(null);
    };

    const create = async () => {
        if (!complete || busy || !colour) return;
        setBusy(true);
        try {
            const body: Record<string, unknown> = { warehouse, colour, uid: userObject.uid, name: userObject.name };
            if (typeStep) body.palletType = palletType;
            if (sizeStep) {
                if (sizeMode === "fixed") body.sizeName = sizeName;
                else body.heightCm = heightValue;
            }
            body.supplierName = supplier.trim();
            if (notesTrimmed) body.notes = notesTrimmed;
            const res = await apiFetch("/pallets", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (bounce(res)) return;
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                const key = data?.reason ? ERROR_KEYS[data.reason] : null;
                toast.error(key ? t(key, { sku: data?.sku ?? "" }) : t("server_error"), { toastId: "pallets-create" });
                return;
            }
            const pallet: PalletRow = data.pallet;
            const chosenHazards = hazardStep && (hazards.liquids || hazards.glass) ? hazards : null;
            const printed = await printPalletLabel(pallet, userObject.name, chosenHazards);
            if (!mountedRef.current) return;
            setResult({ pallet, printed, hazards: chosenHazards });
            if (printed) toast.success(t("pallets_created_toast", { sku: pallet.pallet_sku }), { toastId: "pallets-created" });
            else toast.warn(t("pallets_print_failed_created", { sku: pallet.pallet_sku }), { toastId: "pallets-created" });
            loadRecent();
        } catch (error) {
            console.error(`[pallets] create failed: ${error}`);
            toast.error(t("server_error"), { toastId: "pallets-create" });
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    const reprint = async (pallet: PalletRow) => {
        if (reprinting) return;
        setReprinting(pallet.pallet_sku);
        try {
            const res = await apiFetch(`/pallets/${encodeURIComponent(pallet.pallet_sku)}/reprint`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse }),
            });
            if (bounce(res)) return;
            // The warnings live only in this screen's memory, so only the
            // pallet just created reprints with them.
            const isResult = result?.pallet.pallet_sku === pallet.pallet_sku;
            const ok = await printPalletLabel(pallet, userObject.name, isResult ? result.hazards : null);
            if (ok) {
                toast.success(t("pallets_admin_reprinted"), { toastId: "pallets-reprint" });
                if (isResult) setResult({ pallet, printed: true, hazards: result.hazards });
                loadRecent();
            } else {
                toast.error(t("print_failed"), { toastId: "pallets-reprint" });
            }
        } finally {
            if (mountedRef.current) setReprinting(null);
        }
    };

    const colourMeta = (key: PalletColour) => PALLET_COLOURS.find(c => c.key === key);

    return (
        <div className="page-data page-data-wide pallets-page" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
            <div className="page-title-bar">
                <h1 className="page-title"><Layers size={26} /> {t("pallets")}</h1>
                <div className="pallets-title-actions">
                    <button
                        type="button"
                        className={`pallets-printer-btn${printerOnline ? " pallets-printer-btn--on" : ""}`}
                        onClick={() => setShowPrinterSelect(true)}
                        title={printerOnline ? t("pallets_printer_connected") : t("pallets_no_printer")}
                        aria-label={t("pallets_printer")}
                    >
                        <Printer size={18} />
                        <span>{printerOnline ? t("pallets_printer_connected") : t("pallets_no_printer")}</span>
                    </button>
                    <button className="page-back-btn" onClick={() => navigate("/goods-in")} aria-label={t("back")}>
                        <ArrowLeft size={20} />
                    </button>
                </div>
            </div>
            <p className="pallets-hint">{t("pallets_hint")}</p>

            <div className="pallets-layout">
                <div className="pallets-steps">
                    {/* 1. Colour */}
                    <section className="pallets-step" aria-labelledby="pallets-step-colour">
                        <h2 id="pallets-step-colour" className="pallets-step-title">{stepTitle(stepNo.colour, "pallets_step_colour")}</h2>
                        <div className="pallets-colour-grid">
                            {PALLET_COLOURS.map(c => (
                                <button
                                    key={c.key}
                                    type="button"
                                    className={`pallets-colour-tile${c.outline ? " pallets-colour-tile--outline" : ""}${colour === c.key ? " pallets-colour-tile--active" : ""}`}
                                    style={{ background: c.swatch, color: c.text }}
                                    aria-pressed={colour === c.key}
                                    disabled={busy || !!result}
                                    onClick={() => setColour(c.key)}
                                >
                                    <span className="pallets-colour-name">{t(c.labelKey)}</span>
                                    {c.hintKey && <span className="pallets-colour-hint">{t(c.hintKey)}</span>}
                                </button>
                            ))}
                        </div>
                    </section>

                    {/* 2. Type */}
                    {typeStep && settings && (
                        <section className="pallets-step" aria-labelledby="pallets-step-type">
                            <h2 id="pallets-step-type" className="pallets-step-title">{stepTitle(stepNo.type, "pallets_step_type")}</h2>
                            <div className="pallets-chip-row">
                                {settings.types.map(name => (
                                    <button
                                        key={name}
                                        type="button"
                                        className={`pallets-chip${palletType === name ? " pallets-chip--active" : ""}`}
                                        aria-pressed={palletType === name}
                                        disabled={busy || !!result}
                                        onClick={() => setPalletType(name)}
                                    >
                                        {name}
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* 3. Size */}
                    {sizeStep && settings && (
                        <section className="pallets-step" aria-labelledby="pallets-step-size">
                            <h2 id="pallets-step-size" className="pallets-step-title">{stepTitle(stepNo.size, "pallets_step_size")}</h2>
                            {settings.sizes.length > 0 && settings.allow_height && (
                                <div className="pallets-size-mode" role="tablist">
                                    <button type="button" role="tab" aria-selected={sizeMode === "fixed"} className={`pallets-chip pallets-chip--mode${sizeMode === "fixed" ? " pallets-chip--active" : ""}`} disabled={busy || !!result} onClick={() => setSizeMode("fixed")}>
                                        {t("pallets_size_fixed")}
                                    </button>
                                    <button type="button" role="tab" aria-selected={sizeMode === "height"} className={`pallets-chip pallets-chip--mode${sizeMode === "height" ? " pallets-chip--active" : ""}`} disabled={busy || !!result} onClick={() => setSizeMode("height")}>
                                        {t("pallets_size_height")}
                                    </button>
                                </div>
                            )}
                            {sizeMode === "fixed" && settings.sizes.length > 0 && (
                                <div className="pallets-chip-row">
                                    {settings.sizes.map(name => (
                                        <button
                                            key={name}
                                            type="button"
                                            className={`pallets-chip${sizeName === name ? " pallets-chip--active" : ""}`}
                                            aria-pressed={sizeName === name}
                                            disabled={busy || !!result}
                                            onClick={() => setSizeName(name)}
                                        >
                                            {name}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {sizeMode === "height" && settings.allow_height && (
                                <label className="pallets-height">
                                    <span>{t("pallets_size_height")}</span>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min={1}
                                        max={300}
                                        step={1}
                                        value={heightCm}
                                        placeholder={t("pallets_height_placeholder")}
                                        aria-label={t("height_cm")}
                                        disabled={busy || !!result}
                                        onChange={e => setHeightCm(e.target.value)}
                                    />
                                    <span className="pallets-height-unit">{t("pallets_height_unit")}</span>
                                </label>
                            )}
                        </section>
                    )}

                    {/* 4. Supplier — every active supplier, Amazon preselected */}
                    <section className="pallets-step" aria-labelledby="pallets-step-supplier">
                        <h2 id="pallets-step-supplier" className="pallets-step-title">{stepTitle(supplierNo, "pallets_step_supplier")}</h2>
                        <label className="pallets-select-label">
                            <span>{t("pallets_supplier")}</span>
                            <select
                                className="pallets-select"
                                value={supplier}
                                aria-label={t("pallets_supplier")}
                                disabled={busy || !!result}
                                onChange={e => setSupplier(e.target.value)}
                            >
                                {suppliers.map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </label>
                        <p className="pallets-step-hint">{t("pallets_supplier_hint", { supplier: DEFAULT_SUPPLIER })}</p>
                    </section>

                    {/* 5. Contents warning — liquids / glass, only when the warehouse toggle is on */}
                    {hazardStep && (
                        <section className="pallets-step" aria-labelledby="pallets-step-hazards">
                            <h2 id="pallets-step-hazards" className="pallets-step-title">{stepTitle(hazardNo, "pallets_step_hazards")}</h2>
                            <div className="pallets-chip-row">
                                {PALLET_HAZARDS.map(({ key, labelKey, Icon }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`pallets-chip pallets-chip--hazard${hazards[key] ? " pallets-chip--active" : ""}`}
                                        aria-pressed={hazards[key]}
                                        disabled={busy || !!result}
                                        onClick={() => setHazards(prev => ({ ...prev, [key]: !prev[key] }))}
                                    >
                                        <Icon size={18} /> {t(labelKey)}
                                    </button>
                                ))}
                            </div>
                            <p className="pallets-step-hint">{t("pallets_hazards_hint")}</p>
                        </section>
                    )}

                    {/* 6. Notes — optional free text */}
                    <section className="pallets-step" aria-labelledby="pallets-step-notes">
                        <h2 id="pallets-step-notes" className="pallets-step-title">{stepTitle(notesNo, "pallets_step_notes")}</h2>
                        <textarea
                            className="pallets-notes"
                            value={notes}
                            rows={3}
                            maxLength={NOTES_MAX}
                            placeholder={t("pallets_notes_placeholder")}
                            aria-label={t("pallets_notes")}
                            disabled={busy || !!result}
                            onChange={e => setNotes(e.target.value)}
                        />
                        <p className="pallets-step-hint">{t("pallets_notes_hint")}</p>
                    </section>
                </div>

                <aside className="pallets-side">
                    {!result ? (
                        <div className="pallets-card">
                            <h2 className="pallets-card-title">{t("pallets_summary")}</h2>
                            <dl className="pallets-summary">
                                <dt>{t("pallets_colour")}</dt>
                                <dd>
                                    {colour ? (
                                        <span className="pallets-swatch-label">
                                            <span className="pallets-swatch" style={{ background: colourMeta(colour)?.swatch }} />
                                            {t(colourMeta(colour)?.labelKey ?? "")}
                                        </span>
                                    ) : <em>{t("pallets_not_chosen")}</em>}
                                </dd>
                                {typeStep && (
                                    <>
                                        <dt>{t("pallets_type")}</dt>
                                        <dd>{palletType ?? <em>{t("pallets_not_chosen")}</em>}</dd>
                                    </>
                                )}
                                {sizeStep && (
                                    <>
                                        <dt>{t("pallets_size")}</dt>
                                        <dd>{sizeSummary || <em>{t("pallets_not_chosen")}</em>}</dd>
                                    </>
                                )}
                                <dt>{t("pallets_supplier")}</dt>
                                <dd>{supplierChosen ? supplier : <em>{t("pallets_not_chosen")}</em>}</dd>
                                {hazardStep && (
                                    <>
                                        <dt>{t("pallets_hazards")}</dt>
                                        <dd>
                                            {hazardSummary
                                                ? <span className="pallets-hazard-flag"><AlertTriangle size={14} /> {hazardSummary}</span>
                                                : <em>{t("pallets_hazards_none")}</em>}
                                        </dd>
                                    </>
                                )}
                                <dt>{t("pallets_notes")}</dt>
                                <dd className="pallets-summary-notes">{notesTrimmed || <em>{t("pallets_notes_none")}</em>}</dd>
                            </dl>
                            <button
                                type="button"
                                className="btn btn-primary pallets-print-btn"
                                disabled={!complete || busy || !settings}
                                onClick={create}
                            >
                                <Printer size={20} /> {busy ? t("pallets_printing") : t("pallets_print")}
                            </button>
                            {!printerOnline && (
                                <p className="pallets-side-note"><AlertTriangle size={14} /> {t("pallets_no_printer")}</p>
                            )}
                        </div>
                    ) : (
                        <div className={`pallets-card pallets-card--result${result.printed ? "" : " pallets-card--warn"}`}>
                            <div className="pallets-result-status">
                                {result.printed ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
                                <span>{result.printed ? t("pallets_created_toast", { sku: result.pallet.pallet_sku }) : t("pallets_print_failed_created", { sku: result.pallet.pallet_sku })}</span>
                            </div>
                            <div className="pallets-result-sku" style={{ borderColor: colourMeta(result.pallet.colour)?.swatch }}>
                                {result.pallet.pallet_sku}
                            </div>
                            <div className="pallets-result-meta">
                                {t(colourMeta(result.pallet.colour)?.labelKey ?? "")}
                                {result.pallet.pallet_type ? ` · ${result.pallet.pallet_type}` : ""}
                                {result.pallet.size_label ? ` · ${result.pallet.size_label}` : ""}
                                {result.pallet.supplier_name ? ` · ${result.pallet.supplier_name}` : ""}
                            </div>
                            {result.hazards && (
                                <p className="pallets-result-notes pallets-hazard-flag">
                                    <AlertTriangle size={14} /> {t("pallets_hazards")}: {hazardNames(result.hazards).join(" + ")}
                                </p>
                            )}
                            {result.pallet.notes && (
                                <p className="pallets-result-notes">{result.pallet.notes}</p>
                            )}
                            <p className="pallets-side-note">{t("pallets_put_away_hint")}</p>
                            <div className="pallets-result-actions">
                                <button type="button" className="btn btn-ghost" disabled={!!reprinting} onClick={() => reprint(result.pallet)}>
                                    <RotateCcw size={16} /> {t("pallets_reprint")}
                                </button>
                                <button type="button" className="btn btn-primary" onClick={reset}>
                                    <Plus size={16} /> {t("pallets_new")}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="pallets-card pallets-card--recent">
                        <h2 className="pallets-card-title">{t("pallets_recent")}</h2>
                        {recent.length === 0 ? (
                            <p className="pallets-side-note">{t("pallets_recent_none")}</p>
                        ) : (
                            <ul className="pallets-recent-list">
                                {recent.map(p => (
                                    <li key={p.pallet_sku}>
                                        <span className="pallets-swatch" style={{ background: colourMeta(p.colour)?.swatch }} />
                                        <span className="pallets-recent-sku">{p.pallet_sku}</span>
                                        <span className="pallets-recent-meta" title={p.notes ?? undefined}>
                                            {[p.pallet_type, p.size_label, p.supplier_name].filter(Boolean).join(" · ")}
                                            {p.created_by_name ? ` · ${p.created_by_name}` : ""}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-sm"
                                            disabled={reprinting === p.pallet_sku}
                                            aria-label={`${t("pallets_reprint")} ${p.pallet_sku}`}
                                            onClick={() => reprint(p)}
                                        >
                                            <Printer size={14} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </aside>
            </div>

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={true}
                    printMode={printMode}
                    onSelect={() => { setShowPrinterSelect(false); setPrintMode("network"); setPrinterOnline(true); }}
                    onConnectLocal={() => { setShowPrinterSelect(false); setPrintMode("local"); setPrinterOnline(true); }}
                    onDisconnect={() => { setShowPrinterSelect(false); setPrintMode(null); setPrinterOnline(false); }}
                    onClose={() => { setShowPrinterSelect(false); detectPrinter(); }}
                />
            )}
        </div>
    );
};

export default Pallets;
